import { ChangeEvent, FC, useState } from "react";
import CustomInput from "../../../../custom-components/form-inputs/CustomInput";
import { useTranslation } from "react-i18next";
import MapIconSVG from "../../../resources/svg/MapIconSVG";
import CustomError from "../../../../custom-components/form-inputs/CustomError";
import LocationPointIconSVG from "../../../resources/svg/LocationPointIcon.SVG";
import LocationIconSVG from "../../../resources/svg/LocationIconSVG";
import useScreenSize from "../../../../custom-hooks/base-hooks/useScreenSize";
import { workSearchSortFields } from "../../../../validation-schemas/work-schemas/WorkSearchSortSchema";
import { FormikProps } from "formik";
import { AutocompletedWorkDTO, WorkSearchSortForm } from "../../../../domain/models/dto/WorkDTO";
import SearchIconSVG from "../../../resources/svg/SearchIconSVG";
import AutocompleteTooltip from "../../../resources/components/AutocompleteTooltip";
import useAutocompletedAddress from "../../../../custom-hooks/domain/useAutocompletedAddress";
import AutocompletedWorkTitleTooltip from "../../../resources/components/work-components/AutocompletedWorkTitleTooltip";
import { useAppDispatch } from "../../../../custom-hooks/useAppDispatch";
import { AutocompleteServiceImpl } from "../../../../services/AutocompleteServiceImpl";
import { AUTOCOMPLETE_LIMIT, DEFAULT_OFFSET, DEFAULT_PAGE } from "../../../../domain/constants";

interface CatalogSearchProps {
    form: FormikProps<WorkSearchSortForm>;
}

const CatalogSearch: FC<CatalogSearchProps> = (props) => {
    const { t } = useTranslation();
    const { upTo650, moreOrEqual650, upTo1024, moreOrEqual1024 } = useScreenSize();
    const dispatch = useAppDispatch();

    const handlers = useAutocompletedAddress(props.form);

    const [titles, setTitles] = useState<AutocompletedWorkDTO[]>([]);

    const resetTitles = () => setTitles([]);
    const selectTitle = (value: string) => props.form.setFieldValue("title", value);

    const autocompleteTitle = async (e: ChangeEvent<HTMLInputElement>) => {
        const result = ((
            await dispatch(
                AutocompleteServiceImpl.getAutocompletedWorksDataThunk({
                    page: DEFAULT_PAGE,
                    limit: AUTOCOMPLETE_LIMIT,
                    offset: DEFAULT_OFFSET,
                    title: e.target.value,
                })
            )
        )?.payload || []) as AutocompletedWorkDTO[];
        setTitles(result);
    };

    return (
        <div className="flex flex-col bg-backgroundBlock border border-backgroundButton rounded-[20px] dmax2:rounded-[10px] px-[44px] td4:px-5 tmax4:px-[15px] py-[52px] td4:py-8 tmax4:py-5 w-full">
            <div className="flex justify-between items-start gap-6 border-b border-backgroundButton pb-6 dmax2:pb-4 mb-6 dmax2:mb-4">
                <div className="relative w-full dmin2:mr-6 td4:mr-4">
                    <SearchIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 dmax2:left-4 top-[14px] dmax2:top-[10px] text-textFaded" />
                    <CustomInput
                        form={props.form}
                        name="title"
                        fields={workSearchSortFields}
                        className="w-full h-[60px] dmax2:h-[45px] pl-[70px] dmax2:pl-[52px]"
                        onChange={autocompleteTitle}
                    />
                    <CustomError name="title" form={props.form} className="mt-2 dmax2:mt-1" />
                    <AutocompletedWorkTitleTooltip
                        data={titles}
                        resetCallback={resetTitles}
                        selectCallback={selectTitle}
                    />
                </div>
                {moreOrEqual650 && (
                    <div
                        onClick={() => props.form.handleSubmit()}
                        className="button H16B dmax2:H14B w-[149px] h-[50px] dmax2:h-[40px] transition duration-300"
                    >
                        {t("catalog.search.searchButton")}
                    </div>
                )}
            </div>
            <div className="flex flex-col w-full justify-between">
                <div className="relative flex w-full">
                    <div className="relative w-full dmin2:mr-6 td4:mr-4">
                        <MapIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 dmax2:left-4 top-[14px] dmax2:top-[10px] text-textFaded" />
                        <CustomInput
                            form={props.form}
                            name="address"
                            nestedKey="country"
                            fields={workSearchSortFields.address.schema}
                            className="w-full h-[60px] dmax2:h-[45px] pl-[70px] dmax2:pl-[52px]"
                            onChange={handlers.autocompleteAddress("country")}
                        />
                        <CustomError name="address" nestedKey="country" form={props.form} className="mt-2 dmax2:mt-1" />
                        <AutocompleteTooltip
                            data={handlers.countries}
                            resetCallback={handlers.resetCountries}
                            selectCallback={handlers.selectCountry}
                            className="px-[46px]"
                        />
                    </div>
                    {moreOrEqual650 && (
                        <div className="relative w-full dmin2:mr-6 di6:mr-4">
                            <LocationPointIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 dmax2:left-4 top-[14px] dmax2:top-[10px] text-textFaded" />
                            <CustomInput
                                form={props.form}
                                name="address"
                                nestedKey="city"
                                fields={workSearchSortFields.address.schema}
                                className="w-full h-[60px] dmax2:h-[45px] pl-[70px] dmax2:pl-[52px] mr-6"
                                onChange={handlers.autocompleteAddress("city")}
                            />
                            <CustomError
                                name="address"
                                nestedKey="city"
                                form={props.form}
                                className="mt-2 dmax2:mt-1"
                            />
                            <AutocompleteTooltip
                                data={handlers.cities}
                                resetCallback={handlers.resetCities}
                                selectCallback={handlers.selectCity}
                                className="px-[46px]"
                            />
                        </div>
                    )}
                    {moreOrEqual1024 && (
                        <div className="relative w-full">
                            <LocationIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 dmax2:left-4 top-[14px] dmax2:top-[10px] text-textFaded" />
                            <CustomInput
                                form={props.form}
                                name="address"
                                nestedKey="address"
                                fields={workSearchSortFields.address.schema}
                                className="w-full h-[60px] dmax2:h-[45px] pl-[70px] dmax2:pl-[52px]"
                                onChange={handlers.autocompleteAddress("address")}
                            />
                            <CustomError
                                name="address"
                                nestedKey="address"
                                form={props.form}
                                className="mt-2 dmax2:mt-1"
                            />
                            <AutocompleteTooltip
                                data={handlers.addresses}
                                resetCallback={handlers.resetAddresses}
                                selectCallback={handlers.selectAddress}
                                className="px-[46px]"
                            />
                        </div>
                    )}
                </div>
            </div>
            {upTo650 && (
                <div className="relative w-full dmin2:mr-6 di6:mr-4 tmax4:mt-2">
                    <LocationPointIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 dmax2:left-4 top-[14px] dmax2:top-[10px] text-textFaded" />
                    <CustomInput
                        form={props.form}
                        name="address"
                        nestedKey="city"
                        fields={workSearchSortFields.address.schema}
                        className="w-full h-[60px] dmax2:h-[45px] pl-[70px] dmax2:pl-[52px] mr-6"
                        onChange={handlers.autocompleteAddress("city")}
                    />
                    <CustomError name="address" nestedKey="city" form={props.form} className="mt-2 dmax2:mt-1" />
                    <AutocompleteTooltip
                        data={handlers.cities}
                        resetCallback={handlers.resetCities}
                        selectCallback={handlers.selectCity}
                        className="px-[46px]"
                    />
                </div>
            )}
            {upTo1024 && (
                <div className="relative w-full mt-4 tmax4:mt-2">
                    <LocationIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 absolute left-6 dmax2:left-4 top-[14px] dmax2:top-[10px] text-textFaded" />
                    <CustomInput
                        form={props.form}
                        name="address"
                        nestedKey="address"
                        fields={workSearchSortFields.address.schema}
                        className="w-full h-[60px] dmax2:h-[45px] pl-[70px] dmax2:pl-[52px]"
                        onChange={handlers.autocompleteAddress("address")}
                    />
                    <CustomError name="address" nestedKey="address" form={props.form} className="mt-2 dmax2:mt-1" />
                    <AutocompleteTooltip
                        data={handlers.addresses}
                        resetCallback={handlers.resetAddresses}
                        selectCallback={handlers.selectAddress}
                        className="px-[46px]"
                    />
                </div>
            )}
            {upTo650 && (
                <div
                    onClick={() => props.form.handleSubmit()}
                    className="button H16B dmax2:H14B w-full h-[50px] dmax2:h-[40px] mt-4 transition duration-300"
                >
                    {t("catalog.search.searchButton")}
                </div>
            )}
        </div>
    );
};

export default CatalogSearch;
